<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">
				{{ $t("policies") }}
				<core-pagination-info
					:itemsPerPage="itemsPerPage"
					:numberOfItems="numberOfItems"
					:currentPage="computedPage"
				/>
			</template>
			<!-- Display this only if it's in tenants settings -->
			<template
				slot="actions"
				v-if="
					$store.state.settings.policies.creating.hasApiCreation ||
						$store.state.settings.policies.creating.hasManualCreation
				"
			>
				<!-- <j-button
					v-can="'writePolicies'"
					:to="`${$route.path}/create-policy-crossroads`"
					type="primary"
				>
					<crm-icon name="add" size="small" color="c5" />
					{{ $t("add_policy") }}
				</j-button> -->
			</template>
		</core-layout-headline-and-actions>

		<!-- Search and quick filter -->
		<div class="flex wrap xs-12 xs-left">
			<!-- Search -->
			<div class="flex xs-12 md-5">
				<template v-if="!listing.filter.isFiltering">
					<j-base-input-search
						:placeholder="$t('search_policy_number')"
						:value="computedSearchTerm"
						@input="onSearch($event)"
					/>
					<j-button
						class="ml2"
						v-if="computedSearchTerm"
						type="tertiary"
						@click="cancelSearch"
						>{{ $t("cancel") }}</j-button
					>
				</template>
			</div>

			<!-- Filter toggle  -->
			<div class="flex xs-12 md-7 xs-middle xs-right pt2 pb2">
				<label for="toggle" class="mr2 c6">
					<template>{{ $t("filter") }}</template>
				</label>
				<j-base-toggle
					id="toggle"
					size="small"
					:value="listing.filter.isFiltering"
					@input="handleFitlerOnOff"
				/>
			</div>
		</div>

		<!-- Filter -->
		<div class="xs-12">
			<core-filter
				:isExpanded="listing.filter.isFiltering"
				:value="listing.filter.payload"
				:forceReset="forceFilterReset"
				for="policies"
				@submit="handleFilterSubmit"
			/>
		</div>

		<!-- Data Container -->
		<j-card class="bg5 mt2">
			<j-data-table
				:header="header"
				:list="computedPolicies"
				:sortable="false"
				:isLoading="isLoading"
				@sort="onSort($event)"
			>
				<template slot="list" slot-scope="{ item }">
					<td v-for="(column, i) in header" :key="i">
						<template v-if="i === 0">
							<router-link
								:to="
									`/${$store.state.tenantId}/policies/detail/policy/${item.id}`
								"
								>{{ item[column.field] }}</router-link
							>
						</template>
						<template v-else-if="column.field === 'status'">
							<!-- Red for CANCELLED and Green for ISSUED -->
							<j-tag
								:label="item[column.field]"
								:variant="decideStatusClass(item['originalStatus'])"
							/>
						</template>
						<template v-else>{{ item[column.field] }}</template>
					</td>
				</template>
				<template slot="empty">{{ $t("errors.no_data") }}</template>
			</j-data-table>
		</j-card>

		<!-- Pagination -->
		<div class="flex xs-12 p4 xs-center mb15">
			<j-pagination
				:amount="numberOfItems"
				:perPage="itemsPerPage"
				:activePage="computedPage"
				@click="onPageChange($event)"
			/>
		</div>
	</core-layout-content>
</template>

<script>
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions"
import CorePaginationInfo from "@/modules/core/components/CorePaginationInfo"
import CrmIcon from "@/components/CrmIcon/"
import JHeadline from "@/components/JHeadline/"
import JText from "@/components/JText/"
import JButton from "@/components/JButton/"
import JCard from "@/components/JCard/"
import JDataTable from "@/components/JDataTable/"
import JAvatar from "@/components/JAvatar/"
import JSearch from "@/components/JSearch/"
import JBaseInputSearch from "@/components/JBaseInputSearch/"
import JBaseToggle from "@/components/JBaseToggle/"
import JPagination from "@/components/JPagination/"
import JTag from "@/components/JTag/"
import CoreFilter from "@/modules/core/components/CoreFilter/"
import { mapState } from "vuex"
import dayjs from "dayjs"
import get from "lodash.get"
import debounce from "lodash.debounce"
import api from "../../api/"

export default {
	components: {
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		CorePaginationInfo,
		CrmIcon,
		JText,
		JButton,
		JHeadline,
		JCard,
		JDataTable,
		JAvatar,
		JSearch,
		JPagination,
		JTag,
		JBaseInputSearch,
		JBaseToggle,
		CoreFilter
	},

	data() {
		return {
			isLoading: false,

			// Fetched data
			numberOfItems: 0,
			policies: [],

			// Pagination
			itemsPerPage: 30,

			// Filtering
			filter: {
				where: null // processed payload into where param of graphql query
			},
			forceFilterReset: 0,

			// header of table
			header: [
				{
					field: "policyNumber",
					label: this.$t("policy_number")
				},
				{
					field: "clientName",
					label: this.$t("client")
				},
				{
					field: "insurerName",
					label: this.$t("insurer")
				},
				{
					field: "productName",
					label: this.$t("product")
				},
				{
					field: "premium",
					label: this.$t("premium")
				},
				{
					field: "discountCodes",
					label: this.$t("discount_codes")
				},
				{
					label: this.$t("status"),
					field: "status"
				},
				{
					label: this.$t("issue_date"),
					field: "issueDate"
				},
				{
					label: this.$t("created_at"),
					field: "createdAt"
				}
			]
		}
	},

	computed: {
		...mapState("policies", ["listing"]),

		computedPage() {
			return this.$store.state.policies.listing.page
		},

		computedWhere() {
			return this.$store.state.policies.listing.filter.where
		},

		computedSearchTerm() {
			return this.$store.state.policies.listing.searchTerm
		},

		// What we are showing to user
		computedPolicies() {
			/**
			 * Helper function to format date
			 */
			const formatDate = date =>
				date ? dayjs(date).format(this.$store.state.settings.dateFormat) : null

			/**
			 * Return computed list
			 */
			return this.policies.map(item => ({
				id: item.id,
				policyNumber: item.issuerNumber ? item.issuerNumber : "---",
				startDate: formatDate(item.startDate),
				endDate: formatDate(item.endDate),
				issueDate: item.issueDate
					? dayjs(item.issueDate).format("DD/MM/YYYY HH:mm:ss")
					: "",
				referrerName: get(item, "referrer.name", ""),
				originalStatus: item.status, // based on this show different color
				status: item.status ? this.$t(item.status) : "",
				insurerName: this.isCaseFlow(item)
					? item?.product?.insurer?.name
					: get(item, "acceptedOffer.product.insurer.name"),
				productName: this.isCaseFlow(item)
					? item?.product?.name
					: get(item, "acceptedOffer.product.name"),
				premium: this.isCaseFlow(item)
					? item?.premium?.formattedPrice
					: get(item, "acceptedOffer.premium.formattedPrice"),
				clientName: get(item, "holder.name", "---"),
				source: item.source ? this.$t(`sources.${item.source}`) : null,
				discountCodes: get(item, "acceptedOffer.premium.appliedDiscounts")
					? get(item, "acceptedOffer.premium.appliedDiscounts")
							.map(item => item.code)
							.join(",")
					: null,
				createdAt: item.createdAt
					? dayjs(item.createdAt).format(
							this.$store.state.settings.dateTimeFormat
					  )
					: null,
				// TODO: this is not correct, we should get insuranceType name on product somehow as it's dynamic now
				// API will need to add some fields probably
				insuranceType: this.$t(
					`insurance_types.${get(item, "acceptedOffer.product.productId.type")}`
				)
			}))
		}
	},

	methods: {
		// Decide if policy comes from case flow or quote flow
		// We are checking premium.acceptedOffer:
		// If it has acceptedOffer, it comes from quote flow otherwise from case flow
		isCaseFlow(policy) {
			if (policy?.acceptedOffer) return false
			return true
		},

		onSort(sorting) {
			this.$store.commit("policies/SET_SORTING", sorting)
			this.$store.commit("policies/SET_PAGE", 1)
			this.fetchPolicies()
		},

		onPageChange(pageIndex) {
			this.$store.commit("policies/SET_PAGE", pageIndex)
			this.fetchPolicies()
			this.scrollToTop()
		},

		scrollToTop() {
			this.$scrollTo("body", 300)
		},

		onSearch: debounce(function(value) {
			const payload = value === null || value === "" ? null : value
			this.$store.commit("policies/SET_SEARCH_TERM", value)
			this.$store.commit("policies/SET_PAGE", 1)
			this.fetchPolicies()
		}, 200),

		cancelSearch() {
			this.$store.commit("policies/SET_SEARCH_TERM", null)
			this.$store.commit("policies/SET_PAGE", 1)
			this.onSearch()
		},

		handleFitlerOnOff(value) {
			this.$store.commit("policies/SET_FILTER_IS_FILTERING", value)

			// When we turn off the filter, refetch
			if (value === false) {
				++this.forceFilterReset
				this.$store.commit("policies/SET_PAGE", 1)
				this.$store.commit("policies/SET_FILTER_PAYLOAD", null)
				this.$store.commit("policies/SET_FILTER_WHERE", null)
				this.fetchPolicies()
			}
		},

		handleFilterSubmit({ payload, where }) {
			this.$store.commit("policies/SET_FILTER_PAYLOAD", payload)
			this.$store.commit("policies/SET_FILTER_WHERE", where)
			this.$store.commit("policies/SET_PAGE", 1)
			this.fetchPolicies()
		},

		async fetchPolicies() {
			const { itemsPerPage } = this

			// Sort out sorting (haha)
			const sort = { fieldName: "createdAt", type: "desc" }

			// Pagination
			const skip = (this.computedPage - 1) * itemsPerPage
			const limit = itemsPerPage

			// Build filter aka `where`
			// Either search is active or filter is active, not both
			let where = {}
			if (this.listing.filter.isFiltering) where = this.computedWhere
			if (!this.listing.filter.isFiltering && this.computedSearchTerm)
				where = {
					issuerNumber_contains: this.computedSearchTerm
				}

			// Api call
			this.isLoading = true
			const res = await api.policies({ sort, skip, limit, where })
			this.isLoading = false

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			// Success
			this.numberOfItems = get(res, "data.policies.totalCount")
			this.policies = get(res, "data.policies.list")
		},

		decideStatusClass(status) {
			if (status === "ISSUED") return "success"
			if (status === "CANCELLED") return "error"
			else return "neutral"
		}
	},

	created() {
		this.fetchPolicies()
	}
}
</script>
